import React from 'react';
import { Image } from '@project-stories/elements/Image/Image';

import useShoppingBasketCart from '@src/hooks/useShoppingBasketCart';

export const Icon = ({
  settings,
  iconSource,
  iconName,
  customIcon,
  iconSize,
  iconLink,
  onClick,
  isOverlay,
  zIndex,
  style,
  className,
  alignment,
  ...props
}) => {
  if (!settings) return null;

  const { cartData } = useShoppingBasketCart();

  const {
    icon_source = iconSource || 'icon_name',
    icon_name = iconName || 'star',
    custom_icon = customIcon,
    icon_size = iconSize || 24,
    icon_link = iconLink,
    icon_color,
    override_icon_color,
    show_count
  } = settings;

  // Get alignment classes
  const getAlignmentClasses = () => {
    switch (alignment) {
      case 'center':
        return 'flex justify-center';
      case 'right':
        return 'flex justify-end';
      case 'left':
      default:
        return 'flex justify-start';
    }
  }; // Create the icon element based on source type
  const renderIcon = () => {
    const iconElement = (() => {
      if (icon_source === 'icon_name' && icon_name) {
        return (
          <div
            className={`icon-${icon_name} flex-shrink-0`}
            style={{
              ...(override_icon_color && icon_color ? { color: icon_color } : {}),
              fontSize: `${iconSize || 24}px`
            }}
          ></div>
        );
      } else if (icon_source === 'custom_image' && custom_icon) {
        return (
          <div
            className='flex-shrink-0'
            style={{ width: `${icon_size}px`, height: `${icon_size}px` }}
          >
            <Image
              imageUrl={custom_icon}
              width={icon_size}
              height={icon_size}
              alt='Custom icon'
              classes='w-full h-full'
              fit='contain'
            />
          </div>
        );
      }
      return null;
    })();

    return iconElement;
  };

  const iconContent = (
    <div
      className={`relative ${getAlignmentClasses()}`}
      style={isOverlay ? { zIndex } : {}}
    >
      {renderIcon()}

      {show_count && cartData && cartData.item_count > 0 ? (
        <span className='bg-[#101010] cart-count font-medium text-white text-[10px]/[1.2] rounded-full w-4 h-4 flex items-center justify-center absolute bottom-[calc(100%-7px)] left-[calc(100%-4px)]'>
          {cartData.item_count}
        </span>
      ) : null}
    </div>
  );
  // Handle both onClick and link functionality
  const handleClick = (e) => {
    // If we have an onClick handler, call it
    if (onClick) {
      onClick();
    }

    // If we have a link and no onClick handler, let the link work normally
    // If we have both, prevent default and let onClick handle it
    if (icon_link && onClick) {
      e.preventDefault();
    }
  };

  // If we have either onClick or link functionality, make it interactive
  if (onClick || icon_link) {
    // If we have a link and no onClick handler, use anchor tag
    if (icon_link) {
      const handleLinkClick = (e) => {
        // Check if this is an anchor link (starts with #)
        if (icon_link.startsWith('#')) {
          console.log('Anchor link clicked:', icon_link);
          e.preventDefault();
          const targetId = icon_link.substring(1);
          const targetElement = document.getElementById(targetId);
          
          if (targetElement) {
            console.log('Scrolling to element with ID:', targetId);
            
            // First, ensure html has scroll-behavior: smooth
            document.documentElement.style.scrollBehavior = 'smooth';
            
            // Try multiple methods for better browser compatibility
            try {
              // Method 1: scrollIntoView (most modern)
              targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
              });
            } catch (error) {
              // Method 2: Fallback to window.scrollTo
              const targetPosition = targetElement.offsetTop;
              window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
              });
            }
            
            // Reset scroll behavior after a delay
            setTimeout(() => {
              document.documentElement.style.scrollBehavior = '';
            }, 1000);
          }
        }
        // For external links, let the default behavior happen
      };

      return (
        <a
          href={icon_link}
          className={`${className ? className + ' ' : ''}cursor-pointer transition-opacity block hover:opacity-80`}
          onClick={handleLinkClick}
        >
          {iconContent}
        </a>
      );
    }

    // If we have onClick (with or without link), use div with button behavior
    return (
      <div
        className={`${className ? className + ' ' : ''}cursor-pointer transition-opacity block hover:opacity-80`}
        onClick={handleClick}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
          }
        }}
        style={style}
      >
        {iconContent}
      </div>
    );
  }

  // Return standalone icon
  return iconContent;
};

export default Icon;
